import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChildCheckInResponse } from '@tabita/contracts';
import { formatDateTime } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { kidsApi } from '@/services/kids.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner } from '@/components/ui';
import { CheckInDialog } from './check-in-dialog';
import { CheckoutDialog } from './checkout-dialog';

/**
 * The kids desk: who is checked in right now, and the two actions the desk
 * actually performs — check a child in (and hand over the code) or check
 * one out (by presenting it).
 */
export function KidsListPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [openOnly, setOpenOnly] = useState(true);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [checkingOutFor, setCheckingOutFor] = useState<ChildCheckInResponse | null>(null);

  const query = useQuery({
    queryKey: ['kids', 'checkins', { openOnly }],
    queryFn: ({ signal }) => kidsApi.list({ openOnly, limit: 50 }, signal),
    placeholderData: (previous) => previous,
  });

  const checkIns = query.data?.data ?? [];
  const isStale = query.isPlaceholderData;

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['kids', 'checkins'], exact: false });
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('kids.checkin.title')}</h1>
          {query.data && (
            <p className="page-count">
              {t('kids.checkin.count', { count: checkIns.length })}
              {query.data.pagination.hasMore && '+'}
            </p>
          )}
        </div>

        {can('kids.checkin.create') && (
          <Button variant="primary" onClick={() => setIsCheckingIn(true)}>
            {t('kids.checkin.checkIn')}
          </Button>
        )}
      </header>

      <div className="filters">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(event) => setOpenOnly(event.target.checked)}
          />
          <span>{t('kids.checkin.openOnly')}</span>
        </label>
      </div>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && checkIns.length === 0 && (
        <EmptyState title={t('kids.checkin.empty')} hint={t('kids.checkin.checkIn')} />
      )}

      {checkIns.length > 0 && (
        <div className="table-scroll" data-stale={isStale || undefined}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('people.person.singular')}</th>
                <th scope="col">{t('kids.checkin.guardianDropOff')}</th>
                <th scope="col">{t('kids.checkin.checkedInAt')}</th>
                <th scope="col">{t('kids.checkin.checkedOutAt')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {checkIns.map((checkIn) => (
                <tr key={checkIn.id}>
                  <td>
                    <Link to={`/people/${checkIn.childPersonId}`} className="person-name">
                      {checkIn.childDisplayName}
                    </Link>
                  </td>
                  <td>
                    {checkIn.guardianDropOffDisplayName ?? <span className="cell-empty">—</span>}
                  </td>
                  <td dir="ltr">{formatDateTime(checkIn.checkedInAt, locale)}</td>
                  <td dir="ltr">
                    {checkIn.checkedOutAt ? (
                      formatDateTime(checkIn.checkedOutAt, locale)
                    ) : (
                      <span className="cell-empty">—</span>
                    )}
                  </td>
                  <td>
                    {can('kids.checkin.update') && !checkIn.checkedOutAt && (
                      <Button variant="ghost" onClick={() => setCheckingOutFor(checkIn)}>
                        {t('kids.checkin.checkOut')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCheckingIn && (
        <CheckInDialog
          onClose={() => setIsCheckingIn(false)}
          onCheckedIn={() => {
            setIsCheckingIn(false);
            void refresh();
          }}
        />
      )}

      {checkingOutFor && (
        <CheckoutDialog
          checkInId={checkingOutFor.id}
          childDisplayName={checkingOutFor.childDisplayName}
          onClose={() => setCheckingOutFor(null)}
          onCheckedOut={() => {
            setCheckingOutFor(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}
